import React, { useMemo, useContext } from 'react'
import { AppContext } from '@ctsu/loader';
import SWRCacheViewer from 'protocol-setup/components/SWRCacheViewer';
import { useSetupPermission } from 'protocol-setup/helpers';
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { cloneDeep } from 'lodash';
import { useFetchRequestBrowserData, useLeadGroupLOVs } from 'protocol-setup/redux/useLovs';
import text from 'protocol-setup/constants/text';
import RequestBrowser from './RequestBrowser'
import { requestStatusOptions } from './lov';



export function getLeadGroupLovFromMeta(meta) {

  return cloneDeep(meta.find(x => x.name === 'leadGrp').options);
}

export const getCTMSLAOList = (appConfig = {}) => {
  try {
    const ctms = JSON.parse(appConfig?.CTMS_LPO_LIST || '{}');
    return Array.isArray(ctms?.lpoList) ? ctms.lpoList : [];
  } catch (error) {
    return [];
  }
};

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const getEffectiveUserType = (userTypes = [], userAppRoles = []) => {
  const normalizedUserTypes = userTypes
    .map((type) => String(type).trim().toUpperCase())
    .filter(Boolean);

  if (normalizedUserTypes.includes('CTSU')) return 'CTSU';
  if (normalizedUserTypes.includes('NCI')) return 'CTSU';
  if (normalizedUserTypes.includes('CTMS')) return 'CTMS';
  if (normalizedUserTypes.includes('LPO')) return 'LPO';

  // Fallback: if userTypes is empty and user has PORTAL.IROC_STAFF role
  if (!normalizedUserTypes.length && userAppRoles.includes('PORTAL.IROC_STAFF')) {
    return 'CTSU';
  }

  return null;
};

const RequestBrowserWithFormattedData = ({ rows, leadGroupLov, hasAddSetupPermission }) => {
  const formattedRows = useMemo(() => {
    return rows.map(row => {
      const statusOption = requestStatusOptions.find(option => option.value === row.requestStatus);
      return {
        ...row,
        requestStatusFormatted: statusOption ? statusOption.label : row.requestStatus
      };
    });
  }, [rows]);

  return (
    <RequestBrowser 
      rows={formattedRows} 
      leadGroupLov={leadGroupLov} 
      hasAddSetupPermission={hasAddSetupPermission} 
    />
  );
}


function ProtocolSetup() {
  const { requestBrowserData, isLoading } = useFetchRequestBrowserData();
  const { leadGroupList, isLoading: isLoadingAllLeadGroups } = useLeadGroupLOVs();
  const hasAddSetupPermission = useSetupPermission(ProtectionElements.BROWSER_ADD);
  const appContext = useContext(AppContext);
  const session = appContext?.session;
  const application = appContext?.application;

  const userTypes = useMemo(
    () => ensureArray(session?.userTypes),
    [session?.userTypes]
  );

  const userGroups = useMemo(
    () => ensureArray(session?.userGroups)
      .map((group) => String(group).trim())
      .filter(Boolean),
    [session?.userGroups]
  );

  const userAppRoles = useMemo(
    () => ensureArray(session?.userAppRoles),
    [session?.userAppRoles]
  );

  const effectiveUserType = useMemo(
    () => getEffectiveUserType(userTypes, userAppRoles),
    [userTypes, userAppRoles]
  );

  const filteredData = useMemo(() => {
    const data = requestBrowserData?.data || [];
  
    if (!data.length) return data;

    if (effectiveUserType === 'CTSU') {
      return data;
    }
    if (effectiveUserType === 'CTMS') {
      const ctmsLpoList = getCTMSLAOList(application?.appConfig);
      return data.filter((record) => ctmsLpoList.includes(record.leadGrp));
    }

    if (effectiveUserType === 'LPO') {
      return data.filter((record) => userGroups.includes(record.leadGrp));
    }

    return [];
  }, [requestBrowserData?.data, effectiveUserType, userGroups, application?.appConfig]);

  const filteredLeadGroupLov = useMemo(() => {
    if (!leadGroupList || !leadGroupList.length) return [];

    if (effectiveUserType === 'CTSU') {
      return leadGroupList;
    }

    if (effectiveUserType === 'CTMS') {
      const ctmsLpoList = getCTMSLAOList(application?.appConfig);
      return leadGroupList.filter((option) => ctmsLpoList.includes(option.value));
    }

    if (effectiveUserType === 'LPO') {
      return leadGroupList.filter((option) => userGroups.includes(option.value));
    }

    return [];
  }, [leadGroupList, effectiveUserType, userGroups, application?.appConfig]);

  if (isLoading || isLoadingAllLeadGroups) {
    return <div>{text.browser.loading}</div>;
  }
  


  return (
    <div className='card-wrapper MuiPaper-elevation1 p-4 rounded'>
      <SWRCacheViewer />
      <RequestBrowserWithFormattedData 
        rows={filteredData} 
        leadGroupLov={[...filteredLeadGroupLov]} 
        hasAddSetupPermission={hasAddSetupPermission}
      />
    </div>
  )
}

export default ProtocolSetup
